import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { brandKnowledgeApi, BriefVerdict, Checkin, CheckinProposal, MonthRecap, MonthlyPlan, ScoreRow } from '@/services/brandKnowledgeApi';
import { useAct } from '@/components/growth-desk/data/useAct';
import { channelName, goalName, monthLabel, money, shortDate } from '@/components/growth-desk/data/status';
import { DESK } from '@/components/growth-desk/links';
import { Chip, cx, DeskButton } from '@/components/growth-desk/ui';
import type { ChipTone } from '@/components/growth-desk/ui/Chip';

/**
 * Is the plan working? Each campaign against its plan (results, cost per result against its
 * target, pace of spend), scored by Admeru, and the latest checkpoint check-in with its
 * one-click changes. Early in a month the honest answer is "too early to call".
 */

export const VERDICT: Record<BriefVerdict, { label: string; tone: ChipTone }> = {
  on_track: { label: 'On track', tone: 'ok' },
  behind: { label: 'Behind', tone: 'bad' },
  too_early: { label: 'Too early to call', tone: 'muted' },
  no_data: { label: 'No results yet', tone: 'warn' },
  not_started: { label: 'Not started', tone: 'neutral' },
};
const PACE = { under: 'spending slower than planned', over: 'spending faster than planned', ok: '' };
const CHECKIN_VERDICT: Record<Checkin['verdict'], { label: string; tone: ChipTone }> = {
  on_track: { label: 'On track', tone: 'ok' },
  off_track: { label: 'Needs a change', tone: 'bad' },
  too_early: { label: 'Too early to call', tone: 'muted' },
};
const ACTION_LABEL: Record<CheckinProposal['action'], (p: CheckinProposal) => string> = {
  rewrite: p => `Rewrite “${p.brief_name}”`,
  skip: p => `Skip “${p.brief_name}” and move its budget`,
  pause: p => `Pause “${p.brief_name}”`,
  shift_budget: p => `Move ${p.share_pct}% of the daily budget from “${p.brief_name}” to “${p.to_brief_name}”`,
};

/** Fresh ads for a tiring live campaign: make them, look at them, add them in one click. */
const FreshAdsAction: React.FC<{ plan: MonthlyPlan; briefId: string; name: string }> = ({ plan, briefId, name }) => {
  const { busy, act } = useAct();
  const [keepOld, setKeepOld] = useState(false);
  const r = plan.refreshes?.[briefId];
  if (!r || r.status === 'failed') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {r?.error && <span className="text-xs text-bad">{r.error}</span>}
        <DeskButton size="xs" variant="navy" loading={busy === 'make'}
          onClick={() => act('make', () => brandKnowledgeApi.refreshBrief(briefId), `Making fresh ads for ${name}`)}>
          {r ? 'Try again' : 'Make fresh ads'}</DeskButton>
      </div>
    );
  }
  if (r.added) return <div className="text-xs text-ok">✓ Fresh ads added {shortDate(r.added.at)}{r.added.paused.length ? ', old ones paused' : ''}</div>;
  if (r.status !== 'ready') return <div className="text-xs text-work">Making fresh ads{r.total ? ` · ${r.done || 0} of ${r.total}` : ''}…</div>;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {r.ads.map(a => <a key={a.variant} href={a.url} target="_blank" rel="noreferrer"><img src={a.url} alt={`Fresh ad ${a.variant}`} className="h-24 w-24 rounded-[10px] object-cover" /></a>)}
      </div>
      <label className="flex items-center gap-2 text-xs text-ink-2">
        <input type="checkbox" checked={keepOld} onChange={e => setKeepOld(e.target.checked)} className="h-3.5 w-3.5" />Keep the old ads running too
      </label>
      <div className="flex flex-wrap gap-2">
        <DeskButton size="xs" variant="primary" loading={busy === 'add'}
          onClick={() => act('add', () => brandKnowledgeApi.addFreshAds(r.id, keepOld), 'Fresh ads added to the live campaign')}>Add to the live campaign</DeskButton>
        <DeskButton size="xs" variant="plain" loading={busy === 'discard'}
          onClick={() => act('discard', () => brandKnowledgeApi.discardFreshAds(r.id), 'Set aside')}>Set aside</DeskButton>
      </div>
    </div>
  );
};

/** Google: fresh headlines and descriptions the owner pastes into Google Ads. */
const FreshTextAction: React.FC<{ plan: MonthlyPlan; briefId: string; name: string }> = ({ plan, briefId, name }) => {
  const { busy, act } = useAct();
  const t = plan.fresh_text?.[briefId];
  const copy = (lines: string[]) => navigator.clipboard?.writeText(lines.join('\n')).catch(() => undefined);
  return (
    <div className="flex flex-col gap-2">
      {t && (
        <div className="grid gap-3 rounded-[12px] bg-inset p-3 text-xs sm:grid-cols-2">
          <div><div className="mb-1 font-semibold text-ink">Headlines</div>{t.headlines.map(h => <div key={h}>{h}</div>)}
            <button type="button" className="mt-1 font-semibold text-brand-ink underline" onClick={() => copy(t.headlines)}>Copy</button></div>
          <div><div className="mb-1 font-semibold text-ink">Descriptions</div>{t.descriptions.map(d => <div key={d}>{d}</div>)}
            <button type="button" className="mt-1 font-semibold text-brand-ink underline" onClick={() => copy(t.descriptions)}>Copy</button></div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <DeskButton size="xs" variant={t ? 'plain' : 'navy'} loading={busy === 'text'}
          onClick={() => act('text', () => brandKnowledgeApi.freshAdText(briefId), `Fresh ad text for ${name}`)}>{t ? 'Write it again' : 'Draft fresh ad text'}</DeskButton>
        {t && <span className="text-xs text-ink-3">Paste into the campaign’s ad in Google Ads.</span>}
      </div>
    </div>
  );
};

const resultsWord = (r: ScoreRow) => `${Math.round(r.results)} ${goalName(r.objective).toLowerCase() || 'results'}`;

const Row: React.FC<{ r: ScoreRow; cur: string }> = ({ r, cur }) => {
  const v = VERDICT[r.verdict];
  const pace = r.pace ? PACE[r.pace] : '';
  return (
    <div className="grid gap-x-4 gap-y-1 border-t border-line py-3 first:border-t-0 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] sm:items-center">
      <div className="min-w-0">
        <div className="truncate font-semibold text-ink" title={r.name}>{r.name}</div>
        <div className="text-xs text-ink-3">{channelName(r.channel)} · day {r.days_run} of {r.days}{r.continues ? ' · kept running' : ''}</div>
      </div>
      <div className="text-small text-ink-2">
        {r.out || r.spend ? (
          <>
            <span className="font-money text-ink">{money(r.spend, cur)}</span> of {money(r.expected_spend, cur)} by now
            {r.results > 0 && <> · {resultsWord(r)}{r.cost_per_result != null && <> at <span className="font-money text-ink">{money(r.cost_per_result, cur)}</span>{r.target ? ` (target ${money(r.target, cur)})` : r.break_even ? ` (break-even ${money(r.break_even, cur)})` : ''}</>}</>}
            {r.estimate && r.results > 0 && r.estimate.high > r.estimate.low && <span className="block text-xs text-ink-3">Likely {money(r.estimate.low, cur)} to {money(r.estimate.high, cur)} per result{r.estimate.prior ? ', weighed with your past results' : ''}</span>}
            {r.forecast && r.live && r.forecast.high > r.forecast.low && <span className="block text-xs text-ink-3">On course for {Math.round(r.forecast.low)} to {Math.round(r.forecast.high)} by {shortDate(r.end_date)}</span>}
            {r.lead_quality?.decided ? <span className="block text-xs text-ink-3">{r.lead_quality.good} of {r.lead_quality.decided} judged leads qualified or won{r.cost_per_good ? ` · ${money(r.cost_per_good, cur)} per good lead` : ''}</span> : null}
            {pace && <span className="block text-xs text-warn">{pace}</span>}
          </>
        ) : <>Starts {shortDate(r.start_date)}</>}
      </div>
      <div><Chip tone={v.tone}>{v.label}</Chip></div>
    </div>
  );
};

const Proposal: React.FC<{ plan: MonthlyPlan; c: Checkin; p: CheckinProposal }> = ({ plan, c, p }) => {
  const { busy, act } = useAct();
  if (p.status !== 'open') {
    return (
      <li className="text-small text-ink-3">
        {p.status === 'applied' ? '✓ Done: ' : 'Dismissed: '}{ACTION_LABEL[p.action](p)}
      </li>
    );
  }
  return (
    <li className="flex flex-col gap-2 rounded-[14px] bg-inset p-3.5">
      <div className="text-sm font-semibold text-ink">{ACTION_LABEL[p.action](p)}</div>
      <div className="text-small text-ink-2">{p.why}{p.action === 'rewrite' && p.direction ? ` New version: ${p.direction}` : ''}</div>
      <div className="flex flex-wrap gap-2">
        <DeskButton size="sm" variant="navy" loading={busy === 'apply'}
          onClick={() => act('apply', () => brandKnowledgeApi.applyCheckinChange(plan.id, c.index, p.id), 'Done')}>Do it</DeskButton>
        <DeskButton size="sm" variant="plain" loading={busy === 'dismiss'}
          onClick={() => act('dismiss', () => brandKnowledgeApi.dismissCheckinChange(plan.id, c.index, p.id), 'Dismissed')}>Not now</DeskButton>
      </div>
    </li>
  );
};

export const CheckinCard: React.FC<{ plan: MonthlyPlan; c: Checkin }> = ({ plan, c }) => {
  const v = CHECKIN_VERDICT[c.verdict];
  return (
    <section aria-label={`Check-in ${shortDate(c.date)}`} className="flex flex-col gap-4 rounded-panel bg-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 font-display text-card-title">Check-in · {shortDate(c.date)}</h3>
        <Chip tone={v.tone}>{v.label}</Chip>
      </div>
      <div className="text-small text-ink-3">The plan asked: {c.check}</div>
      <p className="m-0 text-[15px] leading-normal text-ink">{c.summary}</p>
      {c.briefs.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-small text-ink-2">
          {c.briefs.map(b => (
            <li key={b.ref}><span className="font-semibold text-ink">{b.name}:</span> {b.note}
              {b.hypothesis === 'holds' ? ' Its idea holds.' : b.hypothesis === 'fails' ? ' Its idea isn’t working.' : ''}</li>
          ))}
        </ul>
      )}
      {c.proposals.length > 0 && <ul className="m-0 flex list-none flex-col gap-2 p-0">{c.proposals.map(p => <Proposal key={p.id} plan={plan} c={c} p={p} />)}</ul>}
    </section>
  );
};

/** The month against its plan: focus, totals, each campaign scored, then the latest check-in. */
export const PlanVsActual: React.FC<{ plan: MonthlyPlan }> = ({ plan }) => {
  const s = plan.scorecard;
  const latest = plan.checkins?.length ? plan.checkins[plan.checkins.length - 1] : null;
  if (!s || !s.briefs.length) return latest ? <CheckinCard plan={plan} c={latest} /> : null;
  const cur = s.currency || plan.context.currency;
  const counts = s.briefs.reduce<Record<string, number>>((m, r) => ({ ...m, [r.verdict]: (m[r.verdict] || 0) + 1 }), {});
  const next = plan.plan.checkpoints.map(c => c.date).sort().find(d => d > s.as_of);
  return (
    <div className="grid items-start gap-7 [grid-template-columns:repeat(auto-fit,minmax(min(420px,100%),1fr))]">
      <section aria-label="The month against its plan" className="flex flex-col gap-4 rounded-panel bg-card p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="m-0 font-display text-card-title">Is the plan working?</h2>
          <span className="text-small text-ink-3">as of {shortDate(s.as_of)}{next ? ` · next check-in ${shortDate(next)}` : ''}</span>
        </div>
        {s.too_early ? (
          <p className="m-0 text-sm text-ink-2">Too early to call. Campaigns need about five days and enough results before their numbers mean anything.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {(['on_track', 'behind', 'too_early', 'no_data', 'not_started'] as BriefVerdict[]).filter(k => counts[k]).map(k => (
              <Chip key={k} tone={VERDICT[k].tone}>{counts[k]} {VERDICT[k].label.toLowerCase()}</Chip>
            ))}
          </div>
        )}
        {s.focus.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-small text-ink-2">
            {s.focus.map(f => <li key={f.objective}><span className="font-semibold text-ink">{f.objective}</span> · measured by {f.kpi}</li>)}
          </ul>
        )}
        <dl className="m-0 grid grid-cols-3 gap-3 text-small">
          <div><dt className="text-ink-3">Spent</dt><dd className="m-0 font-money text-base text-ink">{money(s.totals.spend, cur)}</dd><dd className="m-0 text-xs text-ink-3">of {money(s.totals.expected_spend, cur)} by now</dd></div>
          <div><dt className="text-ink-3">Results</dt><dd className="m-0 font-money text-base text-ink">{Math.round(s.totals.results)}</dd>
            {s.totals.forecast && s.totals.forecast.high > s.totals.forecast.low && !s.too_early && <dd className="m-0 text-xs text-ink-3">on course for {Math.round(s.totals.forecast.low)}–{Math.round(s.totals.forecast.high)}</dd>}</div>
          <div><dt className="text-ink-3">Per result</dt><dd className="m-0 font-money text-base text-ink">{s.totals.cost_per_result != null ? money(s.totals.cost_per_result, cur) : '—'}</dd></div>
        </dl>
        <div className="flex flex-col">{s.briefs.map(r => <Row key={r.brief_id} r={r} cur={cur} />)}</div>
        {plan.weekly?.flags.length ? (
          <div className="flex flex-col gap-2 border-t border-line pt-3.5">
            <div className="text-label uppercase text-ink-3">This week · {plan.weekly.flags.length} to look at</div>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-small text-ink-2">
              {plan.weekly.flags.map((f, i) => (
                <li key={i} className="flex flex-col gap-1.5"><span><span className="font-semibold text-ink">{f.name}:</span> {f.text}</span>
                  {f.kind === 'fatigue' && f.channel === 'meta' && <FreshAdsAction plan={plan} briefId={f.brief_id} name={f.name} />}
                  {f.kind === 'fatigue' && f.channel === 'google_ads' && <FreshTextAction plan={plan} briefId={f.brief_id} name={f.name} />}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {(() => {
          const flagged = new Set((plan.weekly?.flags || []).filter(f => f.kind === 'fatigue').map(f => f.brief_id));
          const waiting = Object.entries(plan.refreshes || {}).filter(([id, r]) => !r.added && !flagged.has(id));
          if (!waiting.length) return null;
          return (
            <div className="flex flex-col gap-2 border-t border-line pt-3.5">
              <div className="text-label uppercase text-ink-3">Fresh ads</div>
              {waiting.map(([id]) => {
                const name = s.briefs.find(b => b.brief_id === id)?.name || 'A campaign';
                return <div key={id} className="flex flex-col gap-1.5 text-small"><span className="font-semibold text-ink">{name}</span><FreshAdsAction plan={plan} briefId={id} name={name} /></div>;
              })}
            </div>
          );
        })()}
      </section>
      {latest && <CheckinCard plan={plan} c={latest} />}
    </div>
  );
};

/** One line for other pages: how many campaigns are on track, and the latest check-in's verdict. */
export const PlanVerdictLine: React.FC<{ plan: MonthlyPlan; className?: string }> = ({ plan, className }) => {
  const s = plan.scorecard;
  const latest = plan.checkins?.length ? plan.checkins[plan.checkins.length - 1] : null;
  if (!s?.briefs.length && !latest) return null;
  const on = s?.briefs.filter(r => r.verdict === 'on_track').length || 0;
  const behind = s?.briefs.filter(r => r.verdict === 'behind').length || 0;
  const open = latest?.proposals.filter(p => p.status === 'open').length || 0;
  return (
    <div className={cx('text-small', className)}>
      {s?.too_early ? 'Too early to call' : `${on} on track · ${behind} behind`}
      {latest ? ` · check-in ${shortDate(latest.date)}: ${CHECKIN_VERDICT[latest.verdict].label.toLowerCase()}` : ''}
      {open ? <> · <Link to={DESK.home(plan.month)} className="font-semibold underline underline-offset-2">{open} change{open === 1 ? '' : 's'} to decide</Link></> : null}
    </div>
  );
};

const RECAP_VERDICT: Record<MonthRecap['verdict'], { label: string; tone: ChipTone }> = {
  worked: { label: 'It worked', tone: 'ok' },
  mixed: { label: 'Mixed', tone: 'warn' },
  did_not_work: { label: 'It didn’t work', tone: 'bad' },
  too_little_data: { label: 'Too little data', tone: 'muted' },
};

/** The month-end review: headline, what worked and didn't, and the lessons the next plans use. */
export const RecapCard: React.FC<{ plan: MonthlyPlan }> = ({ plan }) => {
  const r = plan.recap;
  if (!r) return null;
  const v = RECAP_VERDICT[r.verdict];
  const cur = r.currency || plan.context.currency;
  return (
    <section aria-label={`How ${monthLabel(plan.month, false)} went`} className="flex flex-col gap-4 rounded-panel bg-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 font-display text-card-title">How {monthLabel(plan.month, false)} went</h2>
        <Chip tone={v.tone}>{v.label}</Chip>
      </div>
      <p className="m-0 text-[17px] font-medium leading-snug text-ink">{r.headline}</p>
      {r.totals && (
        <div className="text-small text-ink-2">
          Spent <span className="font-money text-ink">{money(r.totals.spend, cur)}</span> of {money(r.totals.budget, cur)} · {Math.round(r.totals.results)} results
          {r.totals.cost_per_result != null ? <> at <span className="font-money text-ink">{money(r.totals.cost_per_result, cur)}</span> each</> : null}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {r.worked.length > 0 && <div><div className="mb-1 text-label uppercase text-ok">Worked</div>
          <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-small text-ink-2">{r.worked.map((x, i) => <li key={i}>{x.text}</li>)}</ul></div>}
        {r.did_not_work.length > 0 && <div><div className="mb-1 text-label uppercase text-bad">Didn’t work</div>
          <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-small text-ink-2">{r.did_not_work.map((x, i) => <li key={i}>{x.text}</li>)}</ul></div>}
      </div>
      {r.lessons.length > 0 && <LessonList lessons={r.lessons} title="What the next plans do with it" />}
    </section>
  );
};

export const LessonList: React.FC<{ lessons: { lesson: string; next_time: string; month?: string; confidence?: string }[]; title: string }> = ({ lessons, title }) => (
  <div className="flex flex-col gap-2">
    <div className="text-label uppercase text-ink-3">{title}</div>
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {lessons.map((l, i) => (
        <li key={i} className="rounded-[14px] bg-inset p-3.5 text-small">
          <div className="font-semibold text-ink">{l.lesson}</div>
          <div className="text-ink-2">{l.next_time}{l.month ? ` · from ${monthLabel(l.month, false)}` : ''}</div>
        </li>
      ))}
    </ul>
  </div>
);

const FORMAT_WORD: Record<string, string> = { post: 'Single posts', carousel: 'Carousels', reel: 'Reels', story: 'Stories' };

/** Reach and engagement of the posts Admeru published, by format and message (best first). */
export const PostsResultsCard: React.FC<{ plan: MonthlyPlan }> = ({ plan }) => {
  const r = plan.posts_results;
  if (!r) return null;
  const pct = (x: number | null) => (x == null ? '—' : `${(x * 100).toFixed(1)}%`);
  const List: React.FC<{ title: string; items: typeof r.format; word?: (n: string) => string }> = ({ title, items, word }) => (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="text-label uppercase text-ink-3">{title}</div>
      {items.slice(0, 4).map(x => (
        <div key={x.name} className="flex justify-between gap-3 text-small">
          <span className="min-w-0 truncate text-ink" title={x.name}>{word ? word(x.name) : x.name}</span>
          <span className="shrink-0 font-money text-ink-2">{x.posts} · {x.avg_reach.toLocaleString()} reach · {pct(x.engagement_rate)}</span>
        </div>
      ))}
    </div>
  );
  return (
    <section aria-label="How your posts did" className="flex flex-col gap-4 rounded-panel bg-card p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 font-display text-card-title">How your posts did</h2>
        <span className="text-small text-ink-3">{r.posts} posts · {r.reach.toLocaleString()} people reached · last 90 days</span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <List title="By format" items={r.format} word={n => FORMAT_WORD[n] || n} />
        <List title="By message" items={r.message} />
      </div>
      <div className="text-xs text-ink-3">Posts · reach per post · people who liked, commented, shared or saved. Next month’s plan leans on what engaged best.</div>
    </section>
  );
};

const LANDING_VERDICT = { good: { label: 'Keeps the ads’ promise', tone: 'ok' as ChipTone }, fix_first: { label: 'Fix first', tone: 'warn' as ChipTone }, weak: { label: 'Weak', tone: 'bad' as ChipTone } };

/** The page the ads send people to: what to fix before (or while) the ads run. */
export const LandingCard: React.FC<{ plan: MonthlyPlan }> = ({ plan }) => {
  const { busy, act } = useAct();
  const list = plan.landings?.length ? plan.landings : plan.landing ? [plan.landing] : [];
  const run = () => act('landing', () => brandKnowledgeApi.checkLanding(plan.id), 'Pages checked');
  return (
    <section aria-label="Where your ads send people" className="flex flex-col gap-3.5 rounded-panel bg-card p-6">
      <h2 className="m-0 font-display text-card-title">Where your ads send people</h2>
      {!list.length && (
        <p className="m-0 text-sm text-ink-2">A weak page is the most common reason ads don’t turn into customers. Check speed, phones, the way to respond, your offer and tracking.</p>
      )}
      {list.map(l => {
        const problems = l.checks.filter(c => !c.ok);
        return (
          <div key={l.url} className="flex flex-col gap-2.5 border-t border-line pt-3.5 first-of-type:border-t-0 first-of-type:pt-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <a href={l.final_url} target="_blank" rel="noreferrer" className="min-w-0 truncate text-small font-semibold text-ink underline underline-offset-2">{l.final_url}</a>
              {l.review && <Chip tone={LANDING_VERDICT[l.review.verdict].tone}>{LANDING_VERDICT[l.review.verdict].label}</Chip>}
            </div>
            {l.briefs?.length ? <div className="text-xs text-ink-3">Used by {l.briefs.join(', ')}</div> : null}
            {l.review?.summary && <p className="m-0 text-[15px] leading-normal text-ink">{l.review.summary}</p>}
            {l.review_skipped === 'built_in_browser' && <p className="m-0 text-sm text-ink-2">This page builds itself in the browser, so Admeru can’t read its words and doesn’t judge them. Open it on a phone and check the offer and the button are right at the top.</p>}
            {(l.review?.fixes.length ? l.review.fixes : problems.map(p => p.fix)).slice(0, 4).map((f, i) => (
              <div key={i} className="flex gap-2.5 text-sm leading-snug"><span className="font-bold text-warn">{i + 1}</span><span>{f}</span></div>
            ))}
            <div className="flex flex-wrap gap-1.5">
              {l.checks.map(c => <Chip key={c.key} tone={c.ok ? 'ok' : c.severity === 'bad' ? 'bad' : 'warn'} title={c.ok ? c.text : c.fix}>{c.ok ? '✓' : '!'} {c.key.replace('_', ' ')}</Chip>)}
            </div>
            <div className="text-xs text-ink-3">Checked {shortDate(l.at)}{l.rendered ? ' · as a phone sees it' : l.rendered === false ? ' · from the raw page (the browser wasn’t available; check again later)' : ''}</div>
          </div>
        );
      })}
      <div><DeskButton size="sm" variant={list.length ? 'plain' : 'navy'} loading={busy === 'landing'} onClick={run}>{list.length ? 'Check again' : 'Check my pages'}</DeskButton></div>
    </section>
  );
};
